import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Tooltip, TablePagination, TextField, Button,
    Box, Typography, CircularProgress, Autocomplete,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import ClientService from '../../../Services/client.service';
import EnumsService from '../../../Services/enums.service';
import CustomYearDatePicker from '../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker';
import { formatApiDateForDisplay, parseApiDateToUtcIso } from '../../../Utils/dateParseUtils';

const CELL_SX = { py: 0.5, px: 1, fontSize: 12 };
const HEADER_SX = { ...CELL_SX, fontWeight: 700, backgroundColor: '#BFC8FF', color: '#304FFF' };

/** SortCodeDetails returns { dateRange: { segment: { sortCodeId: { name: [...] } } } } */
const extractSortCodeTransactions = (apiData) => {
    if (!apiData) return [];
    if (Array.isArray(apiData)) return apiData;
    if (Array.isArray(apiData.values)) return apiData.values;
    if (Array.isArray(apiData.data)) return apiData.data;
    if (typeof apiData !== 'object') return [];

    try {
        const firstDict = apiData[Object.keys(apiData)[0]];
        const secondDict = firstDict?.[Object.keys(firstDict || {})[0]];
        const thirdDict = secondDict?.[Object.keys(secondDict || {})[0]];
        const values = thirdDict?.[Object.keys(thirdDict || {})[0]];
        return Array.isArray(values) ? values : [];
    } catch (_) {
        return [];
    }
};

const getTxTypeLabel = (tx, t) => {
    const raw = tx['t-type'] || tx.docType || tx.type;
    if (raw === 'Incomes' || raw === 3 || raw === 'Income') return t('Income') || 'הכנסה';
    if (raw === 'Expenses' || raw === 1) return t('Expense') || 'הוצאה';
    return t('JournalEntry') || 'פקודת יומן';
};

const getTxSelectType = (tx) => {
    const raw = tx['t-type'] || tx.docType || tx.type;
    if (raw === 'Incomes' || raw === 3 || raw === 'Income') return 'Incomes';
    if (raw === 'Expenses' || raw === 1) return 'Expenses';
    return 'JournalEntries';
};

/** Prefer net-of-VAT (t-amount-vat) for asset sell amount; fall back to gross fields. */
const getTxAmount = (tx) => {
    const vatAmount = tx['t-amount-vat'];
    const raw = (vatAmount != null && vatAmount !== '')
        ? vatAmount
        : (tx.amount || tx.Amount || tx['t-amount-no-vat'] || 0);
    const n = Number(raw);
    if (isNaN(n)) return 0;
    return Math.round(n * 100) / 100;
};

const SaleTransactionPicker = ({
    open,
    onClose,
    onSelect,
    caseId,
    accountantId,
    token,
    sortCodeOptions = [],
}) => {
    const { t } = useTranslation();
    const nodeRef = React.useRef(null);

    const [selectedSortCode, setSelectedSortCode] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const initialYear = useMemo(() => new Date(), []);
    const yearRef = useRef(initialYear);
    const [year, setYear] = useState(initialYear);

    const handleYearChange = useCallback((d) => {
        yearRef.current = d;
        setYear(d);
    }, []);

    const getYearDateRange = useCallback((dateVal) => {
        const y = dateVal
            ? new Date(dateVal).getUTCFullYear()
            : new Date().getUTCFullYear();
        return {
            startDate: new Date(Date.UTC(y, 0, 1)),
            endDate: new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999)),
        };
    }, []);

    const defaultSortCode = useMemo(() => {
        return sortCodeOptions.find(sc =>
            sc.sortCodeName?.includes('מכירת ציוד') || sc.sortCodeName?.includes('מכירת צי')
        ) || null;
    }, [sortCodeOptions]);

    React.useEffect(() => {
        if (open && defaultSortCode && !selectedSortCode) {
            setSelectedSortCode(defaultSortCode);
        }
    }, [open, defaultSortCode]);

    const handleFetch = useCallback(async () => {
        const scId = selectedSortCode?.sortCodeId;
        if (!scId || !caseId || !accountantId) return;

        setLoading(true);
        setFetched(false);
        try {
            const { startDate, endDate } = getYearDateRange(yearRef.current ?? year);

            const res = await ClientService.SortCodeDetails(
                caseId, accountantId, startDate, endDate, token, scId, false
            );
            if (!res?.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            const values = extractSortCodeTransactions(data);
            setTransactions(values);
            setFetched(true);
            setPage(0);
        } catch (err) {
            console.error('SaleTransactionPicker fetch error:', err);
            setTransactions([]);
            setFetched(true);
        } finally {
            setLoading(false);
        }
    }, [selectedSortCode, caseId, accountantId, token, year, getYearDateRange]);

    const filtered = useMemo(() => {
        const list = Array.isArray(transactions) ? transactions : [];
        if (!searchTerm.trim()) return list;
        const q = searchTerm.trim().toLowerCase();
        return list.filter(tx => {
            const desc = (tx.description || tx.Description || tx['t-details'] || '').replace(/^null$/i, '');
            const ref = tx.reference || tx.Reference || tx['t-reference'] || '';
            const amount = getTxAmount(tx);
            const docDate = tx.documentDate || tx.DocumentDate || tx['t-document-date'] || '';
            return (
                desc.toLowerCase().includes(q) ||
                ref.toLowerCase().includes(q) ||
                String(amount).includes(q) ||
                docDate.toLowerCase().includes(q)
            );
        });
    }, [transactions, searchTerm]);

    const paginated = useMemo(() => {
        return filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [filtered, page, rowsPerPage]);

    const handleSelect = (tx) => {
        const id = tx.id || tx.Id;
        const type = getTxSelectType(tx);
        const amount = getTxAmount(tx);
        const date = parseApiDateToUtcIso(
            tx.documentDate || tx.DocumentDate || tx['t-document-date']
        );
        onSelect({ id, type, amount, date });
        handleClose();
    };

    const handleClose = () => {
        setTransactions([]);
        setFetched(false);
        setSearchTerm('');
        setPage(0);
        onClose();
    };

    if (!open) return null;

    return (
        <Draggable handle=".drag-handle-sale-picker" nodeRef={nodeRef}>
            <div
                ref={nodeRef}
                style={{
                    position: 'fixed',
                    top: '8%',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    width: '900px',
                    maxHeight: '82vh',
                    overflowY: 'auto',
                    zIndex: 100000,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                    border: '1px solid #c5ceff',
                }}
            >
                <div
                    className="drag-handle-sale-picker"
                    style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        cursor: 'move', marginBottom: 12,
                    }}
                >
                    <Tooltip title={t('Close')}>
                        <IconButton onClick={handleClose}>
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </IconButton>
                    </Tooltip>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1a237e' }}>
                        {t('SelectSaleTransaction') || 'בחר תנועת מכירה'}
                    </Typography>
                    <Box sx={{ width: 40 }} />
                </div>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography sx={{ fontSize: 11, color: '#777', mb: 0.3 }}>{t('SortCode') || 'מיון'}</Typography>
                        <Autocomplete
                            size="small"
                            options={sortCodeOptions}
                            getOptionLabel={(opt) => `${opt.sortCodeId} - ${opt.sortCodeName}`}
                            value={selectedSortCode}
                            onChange={(_, v) => setSelectedSortCode(v)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('SelectSortCode') || 'בחר מיון'}
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            sx={{ '& .MuiAutocomplete-popper': { zIndex: 200000 } }}
                            slotProps={{ popper: { style: { zIndex: 200000 } } }}
                        />
                    </Box>
                    <Box sx={{ width: 140 }}>
                        <Typography sx={{ fontSize: 11, color: '#777', mb: 0.3 }}>{t('Year') || 'שנה'}</Typography>
                        <CustomYearDatePicker
                            date={year}
                            onDateChange={handleYearChange}
                            height="40px"
                        />
                    </Box>
                    <Button
                        variant="contained" size="small"
                        onClick={handleFetch}
                        disabled={!selectedSortCode || loading}
                        sx={{
                            backgroundColor: '#304FFF', textTransform: 'none', borderRadius: '8px',
                            fontSize: 12, px: 3, py: 1,
                            '&:hover': { backgroundColor: '#2040DD' },
                        }}
                    >
                        {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : (t('FetchTransactions') || 'טען תנועות')}
                    </Button>
                </Box>

                {fetched && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontSize: 13, color: '#333' }}>
                                {t('TransactionsFound') || 'תנועות נמצאו'}: <strong>{filtered.length}</strong>
                            </Typography>
                            <TextField
                                size="small"
                                placeholder={t('Search') || 'חיפוש'}
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                                sx={{ width: 220 }}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ color: '#999', fontSize: 18, mr: 0.5 }} />,
                                    sx: { fontSize: 12 },
                                }}
                            />
                        </Box>

                        <TableContainer component={Paper} sx={{ maxHeight: 350, borderRadius: '8px' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={HEADER_SX}>{t('DocumentDate') || 'תאריך'}</TableCell>
                                        <TableCell sx={HEADER_SX}>{t('Description') || 'תיאור'}</TableCell>
                                        <TableCell sx={HEADER_SX}>{t('Reference') || 'אסמכתא'}</TableCell>
                                        <TableCell sx={HEADER_SX}>{t('Amount') || 'סכום'}</TableCell>
                                        <TableCell sx={HEADER_SX}>{t('Type') || 'סוג'}</TableCell>
                                        <TableCell sx={HEADER_SX} align="center">{t('Select') || 'בחר'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginated.map((tx, idx) => {
                                        const docDate = tx.documentDate || tx.DocumentDate || tx['t-document-date'];
                                        const desc = (tx.description || tx.Description || tx['t-details'] || '').replace(/^null$/i, '');
                                        const ref = tx.reference || tx.Reference || tx['t-reference'] || '';
                                        const amount = getTxAmount(tx);
                                        const typeLabel = getTxTypeLabel(tx, t);
                                        return (
                                            <TableRow key={tx.id || tx.Id || idx} hover sx={{ cursor: 'pointer' }} onClick={() => handleSelect(tx)}>
                                                <TableCell sx={CELL_SX}>
                                                    {formatApiDateForDisplay(docDate)}
                                                </TableCell>
                                                <TableCell sx={CELL_SX}>{desc}</TableCell>
                                                <TableCell sx={CELL_SX}>{ref}</TableCell>
                                                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#1565c0' }}>{beautifyNumber(amount, 2)}</TableCell>
                                                <TableCell sx={CELL_SX}>{typeLabel}</TableCell>
                                                <TableCell sx={CELL_SX} align="center">
                                                    <Tooltip title={t('SelectThisTransaction') || 'בחר תנועה זו'}>
                                                        <IconButton size="small" sx={{ color: '#2e7d32' }}>
                                                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {paginated.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#999' }}>
                                                {t('NoTransactionsFound') || 'לא נמצאו תנועות'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            rowsPerPageOptions={[5, 10, 25]}
                            count={filtered.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            labelRowsPerPage={t('RowsPerPage') || 'שורות'}
                        />
                    </>
                )}
            </div>
        </Draggable>
    );
};

export default SaleTransactionPicker;
